
// Pure, DOM/THREE-free capability model for Digitalisering's Shift+Right-
// click context menus (vertex/segment/whole-object/range - see
// DigitizationModule's mouseup handler for how a click resolves to one of
// these, and VectorEditingMenu.js for how a menu is built from the result).
//
// The point of this module: a menu item that doesn't actually apply to the
// current selection must never appear at all (not shown disabled/greyed -
// simply omitted, per the "no disabled placeholders" requirement) - and
// that decision should live in ONE place instead of being re-derived ad
// hoc inside each menu-builder. This is deliberately small - one function
// per selection type, plain booleans - not a general permissions
// framework.
//
// ── Platform-wide context-menu revision ─────────────────────────────────
// `objectKind` generalises this beyond plain FeatureVisual geometry types
// (Point/LineString/Polygon) to every registered object the platform can
// show a context menu for, regardless of how it happens to be implemented
// under the hood:
//   - "point"/"linestring"/"polygon" - ordinary FeatureVisual features,
//     unchanged, keyed by `geometryType` exactly as before.
//   - "crossprofile" - a Tværprofiler feature. Its underlying registered
//     geometry genuinely IS an editable LineString (see crossSectionTool.js -
//     CrossSectionMarkersVisual is only a decorative child), so vertex/
//     segment/range editing is identical to an ordinary Linje; the only
//     difference is its whole-object menu, which additionally offers
//     "Vis rapport" (`canShowReport`) - editing the path and re-opening the
//     report simply regenerates it from the current geometry (see
//     crossSectionTool.js's generateAndShowCrossSectionReport()), so there
//     is no need to restrict the ordinary editing operations to avoid
//     "stale report" risk.
//   - "pipe" - a Rør feature (PipeVisual - a plain THREE.Object3D with no
//     vertex/segment mechanics at all). Only a whole-object menu applies,
//     and only "Slet objekt" - no Pipe-specific operations exist yet.
//
// `objectKind` defaults to `geometryType.toLowerCase()` when omitted, so
// every pre-existing call site/test that only ever dealt with plain
// FeatureVisual geometry types keeps working unchanged.
//
// Unification note (audit finding): "whole feature" (`selectionType:
// "feature"`) and "the whole object selected via 'Vælg hele objektet'"
// (`selectionType: "range"` with `range` spanning every segment) used to be
// two SEPARATE branches below that could disagree on the exact same
// geometry (feature never granted canDistributeEvenly/canFixedAngle, range
// did, for identical LineString geometry) - purely an artifact of which
// menu path the user happened to take, not the geometry itself. Fixed by
// having "feature" internally delegate to the exact same range computation
// as "range" (see resolveWholeLineCapabilities()) - one source of truth for
// "what can the whole line do", independent of how the user got there.
//
// ── Linje-topologi (Del/Saml/Forlæng/Trim/Forbind/Sæt punkt ved skæring) ──
// New cross-feature topology operations (geometry/lineSplit.js) are
// deliberately restricted to `kind === "linestring"` - plain, ordinary
// LineString features - and excluded for "crossprofile"/"pipe"/"polygon"
// even where the underlying geometryType happens to be LineString. A
// Tværprofiler path and a Rør have their own specialised structure/
// lifecycle and are out of this task's scope (see the task's own
// afgrænsning); a Polygon ring has no endpoints to split/extend/trim/
// connect. Split/Sæt punkt ved skæring need to know whether a vertex is an
// INTERIOR one (`vertexIndex`) vs. an endpoint - both are now read from the
// caller-supplied `vertexIndex`/`vertexCount` (previously present in the
// signature but unused for the "vertex" branch).

const NONE = Object.freeze({
	canMoveVertex: false,
	canMoveVertexZ: false,
	canMoveLineZ: false,
	canDeleteVertex: false,
	canSnapToPointCloud: false,
	canSnapToOtherPoint: false,
	canInsertVertex: false,
	canFixedAngle: false,
	canParallel: false,
	canPerpendicular: false,
	canStraighten2D: false,
	canStraighten3D: false,
	canSimplify: false,
	canOrthogonalize: false,
	canReverse: false,
	canDeleteFeature: false,
	canSelectRange: false,
	canSelectWhole: false,
	canDistributeEvenly: false,
	canSnapAllToPointCloud: false,
	canShowReport: false,
	canSplit: false,
	canJoin: false,
	canExtend: false,
	canTrim: false,
	canTrimBridge: false,
	canConnectEndpoint: false,
	canContinueLine: false,
	canIntersectInsert: false,
	canReshape: false,
	canExtendSegment: false,
	canSetAbsoluteZ: false,
	canArcFromSagitta: false,
	canInterpolateZ: false,
	canSetSlope: false,
	canEditArc: false,
	canFlipArc: false,
	canDissolveArc: false,
	canEditParametricShape: false,
	canDissolveParametricShape: false,
});

// Resolves a segment-hit (segmentIndex/t, from _findHoveredSegment()) to the
// one shared vertex "Fast vinkel" would adjust: whichever endpoint of the
// clicked segment the click landed nearer to, provided it actually has a
// segment on both sides (an endpoint of the whole polyline has no "shared
// vertex between two segments" to speak of - falls back to the segment's
// other endpoint, or gives up if neither qualifies, e.g. a bare 2-point
// line). Pure index arithmetic, no coordinates - shared by the capability
// check (is Fast vinkel even applicable here?) and the operation itself
// (VectorEditingMenu.js's resolveFixedAngleVertex(), a thin wrapper around
// this that reads vertexCount off a real FeatureVisual).
export function resolveFixedAngleVertexIndex(vertexCount, segmentIndex, t){
	const hasBothNeighbors = (idx) => idx > 0 && idx < vertexCount - 1;
	const nearer = t < 0.5 ? segmentIndex : segmentIndex + 1;
	if(hasBothNeighbors(nearer)) return nearer;
	const other = t < 0.5 ? segmentIndex + 1 : segmentIndex;
	return hasBothNeighbors(other) ? other : null;
}

// Pure range-capability computation, shared by BOTH `selectionType:"range"`
// (an explicit sub-selection - "Vælg sammenhængende del…") and the
// LineString/crossprofile branch of `selectionType:"feature"` (the WHOLE
// line, expressed as the range spanning every one of its segments) - see
// this file's own "Unification note" above for why these must never
// diverge for the same underlying geometry.
function resolveLineRangeCapabilities(segmentSpan){
	return {
		canStraighten2D: segmentSpan >= 1,
		canStraighten3D: segmentSpan >= 1,
		canSimplify: segmentSpan >= 1,
		canOrthogonalize: segmentSpan >= 2, // needs at least one interior vertex
		canFixedAngle: segmentSpan === 2, // exactly two segments around one shared vertex
		canDistributeEvenly: segmentSpan >= 2, // needs at least one interior vertex to redistribute
		// "Bue fra pilhøjde…"/"Interpolér Z mellem punkter": both only ever
		// touch INTERIOR vertices (the two range endpoints are always the
		// fixed chord/Z endpoints) - with no interior vertex the operation
		// would be a pure no-op, same reasoning as canOrthogonalize above.
		canArcFromSagitta: segmentSpan >= 2,
		canInterpolateZ: segmentSpan >= 2,
		// "Sæt fald/hældning…": recomputes every OTHER vertex from one
		// reference endpoint (task §13), including the far endpoint itself -
		// so it is meaningful for a bare 2-vertex range already (a single
		// segment's own slope), same gate as canStraighten2D/canSimplify.
		canSetSlope: segmentSpan >= 1,
		// "Flyt linje/del i Z…": moves every vertex of the span lodret by one Δz - meaningful for any real span (>= 1 segment).
		canMoveLineZ: segmentSpan >= 1,
	};
}

// {objectKind?, geometryType, selectionType, vertexIndex, segmentIndex,
// segmentT, vertexCount, range, arcRange} -> capability flags. Only the
// fields relevant to `selectionType` need to be supplied by the caller.
// `arcRange` (selectionType "segment" only) is the stored arc-range record
// (from arcRangeStore.js's findArcRangeForSegment()) the clicked segment
// belongs to, or null/omitted - it gates canEditArc/canFlipArc/
// canDissolveArc. `parametricShapeKind` (selectionType "feature" only) is
// "circle" | "rectangle" | null - whether the whole Polygon feature carries
// a parametric shape definition (feature.domainData.circle/.rectangle - see
// geometry/parametricShapeStore.js) - it gates canEditParametricShape/
// canDissolveParametricShape; VectorEditingMenu.js reads the SAME string
// again to pick "Rediger cirkel…" vs "Rediger rektangel…" wording, so this
// module never needs to know the two shapes' details, only that one exists.
// `objectKind` defaults to `geometryType.toLowerCase()` when omitted - see
// this file's own header comment for the full objectKind model
// (point/linestring/polygon/crossprofile/pipe).
//
// Polygon is handled conservatively throughout (task requirement): only
// operations already exercised/verified (or, as of this revision, freshly
// added with an explicit post-condition validity check - see Forenkle/
// Ortogonaliser below and VectorEditingMenu.js's use of
// geometry/polygonValidity.js's isValidPolygonRing()) are enabled -
// everything else topology-sensitive (straighten, reverse, parallel/
// perpendicular-to-reference, range selection, fixed angle) stays disabled
// rather than implemented or guessed at for a closed ring.
function resolveEditingCapabilities({
	objectKind = null,
	geometryType,
	selectionType,
	vertexIndex = null,
	segmentIndex = null,
	segmentT = 0,
	vertexCount = 0,
	range = null,
	arcRange = null,
	parametricShapeKind = null,
} = {}){
	const kind = objectKind || (geometryType ? geometryType.toLowerCase() : null);

	if(selectionType === "vertex"){
		// Snap til nærmeste punktsky punkt / Snap til andet punkt / Flyt punkt i
		// Z all apply uniformly to every geometry type AND every vertex role
		// (task: "kan være til både punkt, vektor og polygon" /
		// "enkeltstående, mellem-vertex og ende vertex") - each only ever
		// touches the one selected vertex, so none of the topology concerns
		// that keep Polygon conservative elsewhere in this file apply here.
		// "Sæt kote…" (absolute Z) applies wherever "Flyt punkt i Z" (relative
		// Z) does - every geometry type, every vertex role (task §11) - same
		// universal gate as canMoveVertexZ.
		const base = {...NONE, canMoveVertex: true, canMoveVertexZ: true, canSetAbsoluteZ: true, canDeleteVertex: true, canSnapToPointCloud: true, canSnapToOtherPoint: true};
		// "measure-fixed" (Mål vinkel/cirkel/azimut): punktantallet ER målingen -
		// et punkt må flyttes/snappes, men aldrig slettes (hele målingen slettes
		// via "Slet objekt")
		if(kind === "measure-fixed") return {...base, canDeleteVertex: false};
		if(kind !== "linestring") return base; // Del linje/Forlæng/Trim/Forbind endpoints: plain LineString only - see this file's header comment

		const isEndpoint = vertexIndex === 0 || vertexIndex === vertexCount - 1;
		const isInteriorVertex = vertexIndex !== null && vertexCount >= 2 && !isEndpoint;

		return {
			...base,
			canSplit: isInteriorVertex, // splitting AT an endpoint would just produce a 1-vertex-shorter copy of the same line - no meaning
			// "Fortsæt linje…" - resumes ordinary line digitization from this
			// endpoint, appending after the last point or prepending before the
			// first (see VectorEditingMenu.js's runContinueLine()). Same
			// endpoint/LineString-only gate as canExtend/canTrim/canConnectEndpoint.
			canContinueLine: isEndpoint && vertexCount >= 2,
			canExtend: isEndpoint && vertexCount >= 2,
			// "Forlæng endesegment…" (fixed distance, own direction) - a sibling
			// of canExtend (til reference), same endpoint/LineString-only gate,
			// but single-feature (see geometry/vectorEditing.js's
			// extendEndpointByDistance() - never reads another feature's geometry).
			canExtendSegment: isEndpoint && vertexCount >= 2,
			canTrim: isEndpoint && vertexCount >= 2,
			canConnectEndpoint: isEndpoint && vertexCount >= 2,
		};
	}

	if(selectionType === "segment"){
		if(kind === "measure-fixed") return {...NONE}; // ingen indsæt/del/parallel osv. på en måling med fast punktantal
		if(geometryType === "Point") return {...NONE};

		if(geometryType === "Polygon"){
			return {...NONE, canInsertVertex: true};
		}

		// The clicked segment belongs to a stored parametric arc range - the
		// caller resolves this via arcRangeStore.js's findArcRangeForSegment()
		// against the feature's own domainData.arc and passes the match (or
		// null) in; this module stays DOM/FeatureManager-free, so it never
		// reads domainData itself (task §10/§19: reuse the existing
		// capability-flag architecture, don't grow it into something that
		// reaches into feature storage on its own).
		const belongsToArc = kind === "linestring" && !!arcRange;

		return {
			...NONE,
			canInsertVertex: true,
			canParallel: true,
			canPerpendicular: true,
			canSelectRange: true,
			canSelectWhole: true,
			canFixedAngle: resolveFixedAngleVertexIndex(vertexCount, segmentIndex, segmentT) !== null,
			// "Flyt segment i Z…": the clicked segment's two vertices move in Z (this branch is LineString-only, Polygon/Point returned above)
			canMoveLineZ: true,
			canSplit: kind === "linestring", // plain LineString only - excludes crossprofile/pipe (this branch never sees Polygon, handled above)
			canIntersectInsert: kind === "linestring",
			// "Trim mod linje" - the picked segment marks the side to KEEP; see
			// geometry/lineSplit.js's trimPolylineAndBridge(). Plain LineString
			// only, same cross-feature-topology restriction as canSplit/
			// canIntersectInsert above.
			canTrimBridge: kind === "linestring",
			// "Rediger bue…"/"Vend bue"/"Opløs bue" (task §7/§18) - only shown
			// when the clicked segment is actually part of a stored arc range.
			canEditArc: belongsToArc,
			canFlipArc: belongsToArc,
			canDissolveArc: belongsToArc,
		};
	}

	if(selectionType === "feature"){
		// Pipe (a PipeVisual - no vertex/segment mechanics at all): only a
		// whole-object menu exists, and only Slet objekt - no Pipe-specific
		// operations exist yet (task's own explicit scope for this
		// iteration). Checked before the geometryType branches below since
		// a Pipe has no meaningful geometryType to speak of.
		if(kind === "pipe") return {...NONE, canDeleteFeature: true};

		// Mål vinkel/cirkel/azimut (fast punktantal): kun hel-objekt-handlinger,
		// der ikke ændrer punktantallet eller geometriens struktur.
		if(kind === "measure-fixed") return {...NONE, canDeleteFeature: true, canSnapAllToPointCloud: true};

		if(geometryType === "Point") return {...NONE}; // fully covered by the vertex menu - "Slet punkt" already removes the whole feature

		if(geometryType === "Polygon"){
			// Snap til nærmeste punktsky punkt (alle punkter), Forenkle and
			// Ortogonaliser are enabled for Polygon - Snap is geometry-type-
			// agnostic (same reasoning as the single-vertex
			// canSnapToPointCloud above: it only ever moves individual
			// vertices, no topology assumption involved); Forenkle/
			// Ortogonaliser are new as of this revision, implemented via a
			// closed-ring-aware adapter (see geometry/vectorEditing.js's
			// simplifyClosedRing()/orthogonalizeClosedRing()) with an
			// isValidPolygonRing() post-condition, so they're safe to offer
			// unconditionally. Everything else stays disabled - straighten/
			// reverse/parallel-perpendicular-to-reference/range-selection
			// have no well-defined meaning for a closed ring and are not
			// implemented.
			return {
				...NONE,
				canSimplify: true, canOrthogonalize: true, canDeleteFeature: true, canSnapAllToPointCloud: true,
				// "Rediger cirkel…/Rediger rektangel…"/"Opløs cirkel/Opløs
				// rektangel" (task DEL B/C) - only when this Polygon actually
				// carries one of those parametric definitions.
				canEditParametricShape: !!parametricShapeKind,
				canDissolveParametricShape: !!parametricShapeKind,
			};
		}

		if(geometryType === "LineString"){
			const segmentSpan = Math.max(vertexCount - 1, 0);
			return {
				...NONE,
				...resolveLineRangeCapabilities(segmentSpan),
				canReverse: true,
				canDeleteFeature: true,
				canSelectRange: true,
				canSnapAllToPointCloud: true,
				// Tværprofiler (kind "crossprofile") is otherwise an ordinary
				// editable LineString - see this file's own header comment -
				// its whole-object menu just additionally offers "Vis rapport".
				canShowReport: kind === "crossprofile",
				canJoin: kind === "linestring",
				// "Trim mod linje" on the whole line: the end with the shortest
				// overshoot past the reference is trimmed (see
				// geometry/lineSplit.js's trimPolylineAndBridge()). Whole-line/
				// single-segment only - deliberately NOT a "range" capability
				// (an arbitrary mid-line range has no well-defined kept side).
				canTrimBridge: kind === "linestring",
			};
		}

		return {...NONE};
	}

	if(selectionType === "range"){
		if(geometryType !== "LineString" || !range) return {...NONE};

		const segmentSpan = range.end - range.start; // segments spanned by the range
		return {
			...NONE,
			...resolveLineRangeCapabilities(segmentSpan),
			// Omtegn del (Reshape): applies to any real range (>=1 segment),
			// same kind-agnostic reasoning as the other same-feature range
			// ops above - a crossprofile's path can be reshaped exactly like
			// an ordinary Linje's; this is NOT a cross-feature topology
			// operation (see this file's header comment), so it is not
			// restricted to kind==="linestring".
			canReshape: segmentSpan >= 1,
		};
	}

	return {...NONE};
}

// ── Integrations-laase (Registreringsprofiler, trin G1) ──────────────────
//
// En Feature med en integrationsbinding (fx en databasebunden registrering) må ikke ændres lokalt på en måde, som
// integrationen ikke kan gennemføre sikkert. To forskellige begrænsninger, begge udtrykt som rene capability-flag
// (modulet kender hverken integration, kilde eller tabel - kalderen oplyser kun `geometryLocked`/`structureLocked`):
//   - geometryLocked:  geometrien kan ikke ændres LIGE NU (midlertidig; åbnes igen, når geometri-writeback findes).
//                      Alle geometriændrende handlinger er utilgængelige.
//   - structureLocked: objektets STRUKTUR må ikke ændres (permanent): handlinger der deler/samler/trimmer/forlænger til
//                      et andet objekt eller skaber/fjerner objekter (rækker) er utilgængelige.
// Sletning af hele objektet (canDeleteFeature), rapport (canShowReport) og "Vælg hele objektet" (canSelectWhole - vejen til
// hel-objekt-menuen med integrationens handlinger) berøres ikke.
const STRUCTURE_FLAGS = ["canSplit", "canJoin", "canExtend", "canTrim", "canTrimBridge", "canConnectEndpoint", "canIntersectInsert"];
const GEOMETRY_FLAGS = [
	"canMoveVertex", "canMoveVertexZ", "canMoveLineZ", "canDeleteVertex", "canSnapToPointCloud", "canSnapToOtherPoint", "canInsertVertex",
	"canFixedAngle", "canParallel", "canPerpendicular", "canStraighten2D", "canStraighten3D", "canSimplify", "canOrthogonalize",
	"canReverse", "canSelectRange", "canDistributeEvenly", "canSnapAllToPointCloud", "canContinueLine",
	"canReshape", "canExtendSegment", "canSetAbsoluteZ", "canArcFromSagitta", "canInterpolateZ", "canSetSlope", "canEditArc",
	"canFlipArc", "canDissolveArc", "canEditParametricShape", "canDissolveParametricShape",
];

function applyEditingLocks(caps, {objectKind = null, geometryType = null, selectionType = null, geometryLocked = false, structureLocked = false} = {}){
	if(!geometryLocked && !structureLocked) return caps;

	const kind = objectKind || (geometryType ? geometryType.toLowerCase() : null);
	const locked = {...caps};

	// et Punkts vertex-menu ER hele objektets menu ("Slet punkt" fjerner featuren) - sletning bevares som objekt-sletning
	if(geometryLocked && selectionType === "vertex" && kind === "point" && caps.canDeleteVertex) locked.canDeleteFeature = true;

	for(const flag of STRUCTURE_FLAGS) locked[flag] = false;
	if(geometryLocked){
		for(const flag of GEOMETRY_FLAGS) locked[flag] = false;
	}

	return locked;
}

// {objectKind?, geometryType, selectionType, ..., geometryLocked?, structureLocked?} -> capability flags. Se
// resolveEditingCapabilities() ovenfor for selv kapabilitetsmodellen og applyEditingLocks() for integrations-laasene.
export function getEditingCapabilities(args = {}){
	return applyEditingLocks(resolveEditingCapabilities(args), args);
}
